import React, { createContext, useContext, useEffect, useState } from "react";
import { JWT_TOKEN } from "../common/constants";
import { jwtDecode } from "jwt-decode";

interface User {
  name: string
}

interface DecodedToken {
  name?: string;
}

interface AuthContextType {
  user: User | null;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const token = localStorage.getItem(JWT_TOKEN);
    if (!token) {
      return null;
    }
    
    const decodedToken = decodeToken(token);
    return {
      name: decodedToken.name
    };
  });

  const login = (token: string) => {
    localStorage.setItem(JWT_TOKEN, token);
    decodeTokenAndSetUser(token, setUser);
  };

  const logout = () => {
    localStorage.removeItem(JWT_TOKEN);
    setUser(null);
  };

  useEffect(() => {
    const handleStorage = () => {
      const token = localStorage.getItem(JWT_TOKEN);
      if (token) {
        decodeTokenAndSetUser(token, setUser);
      } else {
        setUser(null);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [setUser]);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

function decodeToken(token: string | null): DecodedToken {
  if (!token) {
    return {};
  }
  
  const decodedToken: DecodedToken = jwtDecode(token);

  return decodedToken;
}

function decodeTokenAndSetUser(token: string | null, setUser: (user: User) => void) {
  const decodedToken = decodeToken(token);

  if (decodedToken?.name) {
    setUser({
      name: decodedToken.name
    });
  }
}